import React, { useState, useCallback } from 'react';
import {
    View,
    Text,
    StyleSheet,
    ScrollView,
    TouchableOpacity,
    Alert,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useFocusEffect } from '@react-navigation/native';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import { theme } from '../constants/theme';
import { ScreenHeader } from '../components/ScreenHeader';
import { AppModal, ModalType } from '../components/AppModal';
import ApiService from '../services/ApiService';
import { Employee, EmployeeSalarySummary, SalaryTransaction } from '../models/types';
import { formatCurrency, formatDate } from '../utils/helpers';

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const EmployeeDetailScreen = ({ navigation, route }: any) => {
    const { employeeId } = route.params;
    const [salarySummary, setSalarySummary] = useState<EmployeeSalarySummary | null>(null);
    const [transactions, setTransactions] = useState<SalaryTransaction[]>([]);
    const [loading, setLoading] = useState(false);
    const [modal, setModal] = useState<{
        visible: boolean;
        type: ModalType;
        title: string;
        message: string;
        onConfirm?: () => void;
    }>({ visible: false, type: 'info', title: '', message: '' });

    const loadData = async () => {
        setLoading(true);
        try {
            const [summary, txns] = await Promise.all([
                ApiService.getEmployeeSalarySummary(employeeId),
                ApiService.getSalaryTransactions(employeeId),
            ]);
            setSalarySummary(summary);
            setTransactions(txns);
        } catch (error) {
            console.error('Error loading employee data:', error);
            setModal({
                visible: true,
                type: 'error',
                title: 'Error',
                message: 'Failed to load employee data.',
            });
        } finally {
            setLoading(false);
        }
    };

    useFocusEffect(
        useCallback(() => {
            loadData();
        }, [employeeId])
    );

    const handleEdit = () => {
        if (salarySummary?.employee) {
            navigation.navigate('AddEditEmployee', { employee: salarySummary.employee });
        }
    };

    const handleDelete = () => {
        if (!salarySummary?.employee) return;

        setModal({
            visible: true,
            type: 'confirm',
            title: 'Delete Employee',
            message: `Are you sure you want to delete "${salarySummary.employee.name}"? This will also delete all salary records.`,
            onConfirm: async () => {
                try {
                    await ApiService.deleteEmployee(employeeId);
                    setModal(prev => ({ ...prev, visible: false }));
                    navigation.goBack();
                } catch (error) {
                    console.error('Error deleting employee:', error);
                    setModal({
                        visible: true,
                        type: 'error',
                        title: 'Error',
                        message: 'Failed to delete employee.',
                    });
                }
            },
        });
    };

    const handleDeleteTransaction = (transaction: SalaryTransaction) => {
        const monthLabel = getMonthLabel(transaction.month);
        setModal({
            visible: true,
            type: 'confirm',
            title: 'Delete Payment',
            message: `Delete payment of ${formatCurrency(transaction.paidAmount)} for ${monthLabel}?`,
            onConfirm: async () => {
                try {
                    await ApiService.deleteSalaryTransaction(employeeId, transaction.id);
                    setModal(prev => ({ ...prev, visible: false }));
                    loadData(); // Refresh data
                } catch (error) {
                    console.error('Error deleting transaction:', error);
                    setModal({
                        visible: true,
                        type: 'error',
                        title: 'Error',
                        message: 'Failed to delete payment.',
                    });
                }
            },
        });
    };

    const getMonthLabel = (month: string) => {
        const [year, monthStr] = month.split('-');
        const monthNumber = parseInt(monthStr, 10);
        return `${MONTH_NAMES[monthNumber - 1]} ${year}`;
    };

    const getStatusInfo = () => {
        if (!salarySummary) return { color: '#2196F3', text: 'Loading...', icon: 'loading' };

        if (salarySummary.isPending) {
            return { color: '#FF9800', text: 'Pending', icon: 'clock-outline' };
        } else if (salarySummary.isAdvance) {
            return { color: '#4CAF50', text: 'Advance', icon: 'arrow-up-circle-outline' };
        }
        return { color: '#2196F3', text: 'Settled', icon: 'check-circle-outline' };
    };

    const employee = salarySummary?.employee;
    const statusInfo = getStatusInfo();

    return (
        <SafeAreaView style={styles.container} edges={['top']}>
            <ScreenHeader
                title="Employee Details"
                showBackButton={true}
                navigation={navigation}
            />

            <ScrollView
                style={styles.scrollView}
                refreshControl={undefined}
                showsVerticalScrollIndicator={false}
            >
                {/* Employee Header Card - Premium Design */}
                {employee && (
                    <View style={styles.headerCard}>
                        <View style={styles.headerGradient}>
                            <View style={styles.avatarLarge}>
                                <Icon name="account" size={44} color="#FFFFFF" />
                            </View>
                            <View style={styles.headerContent}>
                                <Text style={styles.employeeNameLarge}>{employee.name}</Text>
                                {employee.phone && (
                                    <View style={styles.phoneRow}>
                                        <Icon name="phone" size={14} color="rgba(255,255,255,0.8)" />
                                        <Text style={styles.phoneText}>{employee.phone}</Text>
                                    </View>
                                )}
                                <View style={styles.salaryBadge}>
                                    <Icon name="currency-inr" size={14} color="#FFFFFF" />
                                    <Text style={styles.salaryBadgeText}>
                                        {formatCurrency(employee.fixedSalary)}/month
                                    </Text>
                                </View>
                            </View>
                        </View>
                        <View style={styles.actionButtons}>
                            <TouchableOpacity style={styles.actionBtn} onPress={handleEdit}>
                                <Icon name="pencil" size={20} color="#2196F3" />
                                <Text style={styles.actionBtnText}>Edit</Text>
                            </TouchableOpacity>
                            <TouchableOpacity
                                style={[styles.actionBtn, styles.deleteActionBtn]}
                                onPress={handleDelete}
                            >
                                <Icon name="trash-can-outline" size={20} color={theme.colors.error} />
                                <Text style={[styles.actionBtnText, styles.deleteText]}>Delete</Text>
                            </TouchableOpacity>
                        </View>
                    </View>
                )}

                {/* Balance Overview Cards - Glassmorphism Style */}
                {salarySummary && (
                    <View style={styles.balanceSection}>
                        <View style={styles.balanceRow}>
                            <View style={[styles.balanceCard, styles.pendingCard]}>
                                <View style={styles.balanceIconContainer}>
                                    <Icon name="clock-outline" size={22} color={theme.colors.textSecondary} />
                                </View>
                                <Text style={styles.balanceLabel}>Pending</Text>
                                <Text style={styles.balanceValue}>
                                    {formatCurrency(salarySummary.pendingAmount)}
                                </Text>
                            </View>
                            <View style={[styles.balanceCard, styles.advanceCard]}>
                                <View style={styles.balanceIconContainer}>
                                    <Icon name="arrow-up-circle-outline" size={22} color={theme.colors.textSecondary} />
                                </View>
                                <Text style={styles.balanceLabel}>Advance</Text>
                                <Text style={styles.balanceValue}>
                                    {formatCurrency(salarySummary.advanceAmount)}
                                </Text>
                            </View>
                        </View>

                        {/* Net Balance Card */}
                        <View style={[styles.netBalanceCard, { borderColor: statusInfo.color + '40' }]}>
                            <View style={styles.netBalanceHeader}>
                                <Text style={styles.netBalanceLabel}>Net Balance</Text>
                                <View style={[styles.statusChip, { backgroundColor: statusInfo.color + '20' }]}>
                                    <Icon name={statusInfo.icon} size={14} color={statusInfo.color} />
                                    <Text style={[styles.statusChipText, { color: statusInfo.color }]}>
                                        {statusInfo.text}
                                    </Text>
                                </View>
                            </View>
                            <Text style={styles.netBalanceValue}>
                                {formatCurrency(Math.abs(salarySummary.netBalance))}
                            </Text>
                            <View style={styles.netBalanceDetails}>
                                <View style={styles.detailItem}>
                                    <Text style={styles.detailLabel}>Total Fixed</Text>
                                    <Text style={styles.detailValue}>
                                        {formatCurrency(salarySummary.totalFixedSalary)}
                                    </Text>
                                </View>
                                <View style={styles.detailDivider} />
                                <View style={styles.detailItem}>
                                    <Text style={styles.detailLabel}>Total Paid</Text>
                                    <Text style={styles.detailValue}>
                                        {formatCurrency(salarySummary.totalPaid)}
                                    </Text>
                                </View>
                            </View>
                        </View>
                    </View>
                )}

                {/* Payment History Section */}
                <View style={styles.historySection}>
                    <View style={styles.sectionHeader}>
                        <Text style={styles.sectionTitle}>Payment History</Text>
                        <Text style={styles.sectionSubtitle}>{transactions.length} payments</Text>
                    </View>

                    {transactions.length > 0 ? (
                        transactions.map((transaction) => (
                            <View key={transaction.id} style={styles.transactionCard}>
                                <View style={styles.transactionLeft}>
                                    <View style={styles.transactionIcon}>
                                        <Icon name="cash-check" size={18} color={theme.colors.textSecondary} />
                                    </View>
                                    <View style={styles.transactionInfo}>
                                        <Text style={styles.transactionMonth}>
                                            {getMonthLabel(transaction.month)}
                                        </Text>
                                        <Text style={styles.transactionDate}>
                                            Paid on {formatDate(transaction.date)}
                                        </Text>
                                        {transaction.notes && (
                                            <Text style={styles.transactionNotes} numberOfLines={1}>
                                                {transaction.notes}
                                            </Text>
                                        )}
                                    </View>
                                </View>
                                <View style={styles.transactionRight}>
                                    <Text style={styles.transactionAmount}>
                                        {formatCurrency(transaction.paidAmount)}
                                    </Text>
                                    <TouchableOpacity
                                        style={styles.deleteTransactionBtn}
                                        onPress={() => handleDeleteTransaction(transaction)}
                                    >
                                        <Icon name="trash-can-outline" size={18} color={theme.colors.error} />
                                    </TouchableOpacity>
                                </View>
                            </View>
                        ))
                    ) : (
                        <View style={styles.emptyContainer}>
                            <View style={styles.emptyIconContainer}>
                                <Icon name="cash-remove" size={48} color={theme.colors.textLight} />
                            </View>
                            <Text style={styles.emptyTitle}>No Payments Yet</Text>
                            <Text style={styles.emptyText}>
                                Tap the + button to record a salary payment
                            </Text>
                        </View>
                    )}
                </View>

                <View style={{ height: 100 }} />
            </ScrollView>

            {/* Floating Action Button */}
            <TouchableOpacity
                style={styles.fab}
                onPress={() => navigation.navigate('AddSalaryPayment', {
                    employeeId,
                    employeeName: employee?.name,
                    fixedSalary: employee?.fixedSalary
                })}
                activeOpacity={0.8}
            >
                <Icon name="plus" size={28} color="#FFFFFF" />
            </TouchableOpacity>

            <AppModal
                visible={modal.visible}
                type={modal.type}
                title={modal.title}
                message={modal.message}
                onClose={() => setModal(prev => ({ ...prev, visible: false }))}
                onConfirm={modal.onConfirm}
            />
        </SafeAreaView>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: '#F5F7FA',
    },
    scrollView: {
        flex: 1,
    },
    // Header Card Styles
    headerCard: {
        margin: theme.spacing.md,
        borderRadius: 20,
        overflow: 'hidden',
        backgroundColor: theme.colors.surface,
        ...theme.shadows.md,
    },
    headerGradient: {
        backgroundColor: '#2196F3',
        padding: theme.spacing.lg,
        flexDirection: 'row',
        alignItems: 'center',
    },
    avatarLarge: {
        width: 72,
        height: 72,
        borderRadius: 36,
        backgroundColor: 'rgba(255,255,255,0.25)',
        justifyContent: 'center',
        alignItems: 'center',
        marginRight: theme.spacing.md,
    },
    headerContent: {
        flex: 1,
    },
    employeeNameLarge: {
        fontSize: 18,
        fontWeight: '600',
        color: '#FFFFFF',
        marginBottom: 4,
    },
    phoneRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        marginBottom: 8,
    },
    phoneText: {
        fontSize: 14,
        color: 'rgba(255,255,255,0.9)',
    },
    salaryBadge: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: 'rgba(255,255,255,0.2)',
        paddingHorizontal: 12,
        paddingVertical: 6,
        borderRadius: 20,
        alignSelf: 'flex-start',
        gap: 4,
    },
    salaryBadgeText: {
        fontSize: 12,
        fontWeight: '500',
        color: '#FFFFFF',
    },
    actionButtons: {
        flexDirection: 'row',
        padding: theme.spacing.sm,
        backgroundColor: theme.colors.surface,
    },
    actionBtn: {
        flex: 1,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        paddingVertical: theme.spacing.sm,
        gap: 6,
    },
    deleteActionBtn: {
        borderLeftWidth: 1,
        borderLeftColor: theme.colors.divider,
    },
    actionBtnText: {
        fontSize: 14,
        fontWeight: '600',
        color: '#2196F3',
    },
    deleteText: {
        color: theme.colors.error,
    },
    // Balance Section Styles
    balanceSection: {
        paddingHorizontal: theme.spacing.md,
        marginBottom: theme.spacing.sm,
    },
    balanceRow: {
        flexDirection: 'row',
        gap: theme.spacing.sm,
        marginBottom: theme.spacing.sm,
    },
    balanceCard: {
        flex: 1,
        padding: theme.spacing.md,
        borderRadius: 16,
        alignItems: 'center',
        ...theme.shadows.sm,
    },
    pendingCard: {
        backgroundColor: '#FAFAFA',
    },
    advanceCard: {
        backgroundColor: '#FAFAFA',
    },
    balanceIconContainer: {
        width: 40,
        height: 40,
        borderRadius: 20,
        backgroundColor: 'rgba(255,255,255,0.8)',
        justifyContent: 'center',
        alignItems: 'center',
        marginBottom: 6,
    },
    balanceLabel: {
        fontSize: 12,
        color: theme.colors.textSecondary,
        marginBottom: 4,
    },
    balanceValue: {
        fontSize: 15,
        fontWeight: '600',
        color: theme.colors.text,
    },
    netBalanceCard: {
        backgroundColor: theme.colors.surface,
        borderRadius: 16,
        padding: theme.spacing.lg,
        borderWidth: 2,
        ...theme.shadows.sm,
    },
    netBalanceHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: theme.spacing.sm,
    },
    netBalanceLabel: {
        fontSize: 13,
        color: theme.colors.textSecondary,
        fontWeight: '500',
    },
    statusChip: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: 10,
        paddingVertical: 4,
        borderRadius: 12,
        gap: 4,
    },
    statusChipText: {
        fontSize: 12,
        fontWeight: '600',
    },
    netBalanceValue: {
        fontSize: 22,
        fontWeight: '600',
        color: theme.colors.text,
        marginBottom: theme.spacing.md,
    },
    netBalanceDetails: {
        flexDirection: 'row',
        backgroundColor: '#F5F7FA',
        borderRadius: 12,
        padding: theme.spacing.sm,
    },
    detailItem: {
        flex: 1,
        alignItems: 'center',
    },
    detailDivider: {
        width: 1,
        backgroundColor: theme.colors.divider,
    },
    detailLabel: {
        fontSize: 11,
        color: theme.colors.textSecondary,
        marginBottom: 2,
    },
    detailValue: {
        fontSize: 14,
        fontWeight: '600',
        color: theme.colors.text,
    },
    // History Section Styles
    historySection: {
        paddingHorizontal: theme.spacing.md,
    },
    sectionHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: theme.spacing.sm,
    },
    sectionTitle: {
        fontSize: 15,
        fontWeight: '600',
        color: theme.colors.text,
    },
    sectionSubtitle: {
        fontSize: 13,
        color: theme.colors.textSecondary,
    },
    transactionCard: {
        backgroundColor: theme.colors.surface,
        borderRadius: 14,
        padding: theme.spacing.md,
        marginBottom: theme.spacing.sm,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        ...theme.shadows.sm,
    },
    transactionLeft: {
        flexDirection: 'row',
        alignItems: 'center',
        flex: 1,
    },
    transactionIcon: {
        width: 36,
        height: 36,
        borderRadius: 18,
        backgroundColor: '#F0F0F0',
        justifyContent: 'center',
        alignItems: 'center',
        marginRight: theme.spacing.sm,
    },
    transactionInfo: {
        flex: 1,
    },
    transactionMonth: {
        fontSize: 13,
        fontWeight: '600',
        color: theme.colors.text,
    },
    transactionDate: {
        fontSize: 11,
        color: theme.colors.textSecondary,
        marginTop: 2,
    },
    transactionNotes: {
        fontSize: 11,
        color: theme.colors.textLight,
        marginTop: 2,
        fontStyle: 'italic',
    },
    transactionRight: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: theme.spacing.sm,
    },
    transactionAmount: {
        fontSize: 14,
        fontWeight: '600',
        color: theme.colors.text,
    },
    deleteTransactionBtn: {
        width: 36,
        height: 36,
        borderRadius: 18,
        backgroundColor: '#FFEBEE',
        justifyContent: 'center',
        alignItems: 'center',
    },
    // Empty State Styles
    emptyContainer: {
        alignItems: 'center',
        paddingVertical: theme.spacing.xl * 2,
    },
    emptyIconContainer: {
        width: 80,
        height: 80,
        borderRadius: 40,
        backgroundColor: '#F5F7FA',
        justifyContent: 'center',
        alignItems: 'center',
        marginBottom: theme.spacing.md,
    },
    emptyTitle: {
        fontSize: 18,
        fontWeight: '600',
        color: theme.colors.text,
        marginBottom: theme.spacing.xs,
    },
    emptyText: {
        fontSize: 14,
        color: theme.colors.textSecondary,
        textAlign: 'center',
    },
    // FAB Styles
    fab: {
        position: 'absolute',
        right: theme.spacing.lg,
        bottom: theme.spacing.lg,
        width: 60,
        height: 60,
        borderRadius: 30,
        backgroundColor: theme.colors.primary,
        alignItems: 'center',
        justifyContent: 'center',
        ...theme.shadows.lg,
        elevation: 8,
    },
});

export default EmployeeDetailScreen;
